import { useMemo } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { Month, Present, Banner } from "@similarweb/icons";
import type { ActivationAlertsContractDisplayAdAlert } from "@similarweb/pro-api-client";
import { shortMonthAndDay, fullMonthDayAndYear } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ImagePlaceholder } from "../../common/image-fallback/ImagePlaceholder";
import { ImageWithFallback } from "../../common/image-fallback/ImageWithFallback";
import { VideoThumbnailPlaceholder } from "../../common/image-fallback/VideoThumbnailPlaceholder";
import { InfoItem } from "../../common/InfoItem";
import { OpenInNewTab } from "../../common/OpenInNewTab";
import { PreviewCard } from "../../common/preview-card/PreviewCard";
import { FooterWrapper, ImageContainer } from "../../common/styledComponents";

export const adCardSize = {
    width: 319,
    height: 200,
};

interface IDisplayAdCardProps {
    ad: ActivationAlertsContractDisplayAdAlert;
    onClick: () => void;
}

export const DisplayAdCard = ({ ad, onClick }: IDisplayAdCardProps) => {
    const { translate } = useTranslate();
    const isVideo = "adType" in ad && ad.adType === "Video";
    const TypeIcon = isVideo ? Present : Banner;
    const typeLabel = isVideo ? "Video" : "Banner";

    const { shortDate, fullDate } = useMemo(() => {
        const date = dayjs(ad.firstSeen);

        return {
            shortDate: date.format(shortMonthAndDay),
            fullDate: date.format(fullMonthDayAndYear),
        };
    }, [ad.firstSeen]);

    return (
        <PreviewCard
            onClick={onClick}
            data-automation="display-ad-card"
            style={adCardSize}
        >
            <ImageContainer padding="16px 16px 0">
                <ImageWithFallback
                    src={ad.imageUrl}
                    alt={ad.title ?? "Ad creative"}
                    fallback={isVideo ? <VideoThumbnailPlaceholder /> : <ImagePlaceholder />}
                />
            </ImageContainer>
            <FooterWrapper data-automation="ad-card-footer">
                <Flex gap="12px">
                    <InfoItem
                        icon={TypeIcon}
                        title={typeLabel}
                        tooltip={translate("activation.alerts.cards.social-post.creative-type", { type: typeLabel })}
                    />
                    <InfoItem
                        icon={Month}
                        title={shortDate}
                        tooltip={translate("activation.alerts.cards.social-post.first-seen", {
                            date: fullDate,
                        })}
                    />
                </Flex>
                <Tooltip content={translate("activation.alerts.cards.ads.landing-page")}>
                    <OpenInNewTab
                        url={ad.landingPage}
                        data-automation="open-landing-page-button"
                    />
                </Tooltip>
            </FooterWrapper>
        </PreviewCard>
    );
};
